'use client'
import * as React from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import type { Category } from 'cf-grpc/creativefabrica/content/v2/types_pb'
import { Link } from '@repo/web-ui/components/link'

import { useMenu } from './context'
import { tw } from 'utils/classnames'
import { stripHTML } from 'utils/string'

interface MenuItemsProps {
  category: Category
  categoryPath: string
  linkPrefix?: string
}

export function MenuItem({
  category,
  categoryPath,
  linkPrefix = '/subscriptions',
}: MenuItemsProps) {
  const { allOpen, mobileOpen } = useMenu()
  const [isOpen, setIsOpen] = React.useState(true)
  const [prevAllOpen, setPrevAllOpen] = React.useState(allOpen)

  if (allOpen !== prevAllOpen) {
    setPrevAllOpen(allOpen)
    setIsOpen(allOpen)
  }

  const categoryName = stripHTML(category.name.replace(/\\'/g, "'"))
  const hasChildren = category.children.length > 0

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={setIsOpen}
      className={tw('mb-2 hidden md:block', mobileOpen && 'block')}
    >
      <Collapsible.Trigger asChild>
        <h3
          data-testid="subcategory-header"
          className={tw(
            'mb-2 cursor-pointer font-bold uppercase',
            hasChildren && "after:ml-1 after:content-['+'] data-[state=open]:after:content-['-']",
          )}
          dangerouslySetInnerHTML={{ __html: categoryName }}
        />
      </Collapsible.Trigger>
      <Collapsible.Content>
        <ul className="mb-2">
          {category.children.map(subcategory => {
            const subcategoryName = stripHTML(subcategory.name.replace(/\\'/g, "'"))
            return (
              <li key={subcategory.slug} className="last:mb-4">
                <Link
                  data-testid="subcategory-link"
                  href={`${linkPrefix}/${categoryPath}/${category.slug}/${subcategory.slug}/`}
                  dangerouslySetInnerHTML={{
                    __html: subcategoryName,
                  }}
                />
              </li>
            )
          })}
        </ul>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
